import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type AdminProduct, type AdminProductVariant, getAdminProducts } from '@/api/productApi';
import CatalogManagementSection from '../components/CatalogManagementSection';
import ProductStockModal, { type ProductStockDraft } from '../components/ProductStockModal';
import ProductTable from '../components/ProductTable';
import ProductToolbar from '../components/ProductToolbar';
import { useCatalogEntities } from '../hooks/useCatalogEntities';
import {
  type AdminProductRow,
  type StockStatus,
  filterProductRows,
  getPaginationRange,
  getStockStatus,
  mapProductsToRows,
} from '../productManagement';

const PAGE_SIZE = 25;

const AdminProductsPage: React.FC = () => {
  const { t } = useTranslation();
  const [productRows, setProductRows] = useState<AdminProductRow[]>([]);
  const [catalogProducts, setCatalogProducts] = useState<AdminProduct[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [pageProductCount, setPageProductCount] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState('');
  const [isStockModalOpen, setIsStockModalOpen] = useState(false);
  const [draftNotice, setDraftNotice] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedBrandId, setSelectedBrandId] = useState('');
  const [selectedStockStatus, setSelectedStockStatus] = useState<StockStatus | ''>('');
  const temporaryId = useRef(-1);
  const listRequestId = useRef(0);
  const {
    brands,
    categories,
    isBrandLoading,
    isCategoryLoading,
    brandError,
    categoryError,
    pendingAction,
    loadCatalogEntities,
    saveBrand,
    removeBrand,
    saveCategory,
    removeCategory,
  } = useCatalogEntities();

  const loadProducts = useCallback(async (page: number) => {
    const requestId = ++listRequestId.current;
    setIsLoading(true);
    setError('');
    try {
      const response = await getAdminProducts(page, PAGE_SIZE);
      if (requestId !== listRequestId.current) return;
      setProductRows(mapProductsToRows(response.data));
      setCatalogProducts(response.data);
      setPageProductCount(response.data.length);
      setPageSize(response.pageSize || PAGE_SIZE);
      setTotalElements(response.totalElements);
      setTotalPages(response.totalPages);
      setHasLoaded(true);
    } catch {
      if (requestId !== listRequestId.current) return;
      setError(t('admin.products.error.load'));
      setHasLoaded(true);
    } finally {
      if (requestId === listRequestId.current) setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadProducts(currentPage);
    return () => {
      listRequestId.current += 1;
    };
  }, [currentPage, loadProducts]);

  useEffect(() => {
    if (!isCategoryLoading && selectedCategoryId && !categories.some((category) => String(category.id) === selectedCategoryId)) {
      setSelectedCategoryId('');
    }
  }, [categories, isCategoryLoading, selectedCategoryId]);

  useEffect(() => {
    if (!isBrandLoading && selectedBrandId && !brands.some((brand) => String(brand.id) === selectedBrandId)) {
      setSelectedBrandId('');
    }
  }, [brands, isBrandLoading, selectedBrandId]);

  const selectedCategoryName = categories.find((category) => String(category.id) === selectedCategoryId)?.name;
  const selectedBrandName = brands.find((brand) => String(brand.id) === selectedBrandId)?.name;
  const hasActiveFilters = Boolean(searchValue.trim() || selectedCategoryId || selectedBrandId || selectedStockStatus);

  const filteredRows = useMemo(() => filterProductRows(productRows, {
    search: searchValue,
    categoryName: selectedCategoryName,
    brandName: selectedBrandName,
    stockStatus: selectedStockStatus,
  }), [productRows, searchValue, selectedBrandName, selectedCategoryName, selectedStockStatus]);

  const clearFilters = () => {
    setSearchValue('');
    setSelectedCategoryId('');
    setSelectedBrandId('');
    setSelectedStockStatus('');
  };

  const saveStockDraft = (draft: ProductStockDraft) => {
    if (draft.mode === 'existingVariant' && draft.productVariantId !== null) {
      setProductRows((currentRows) => currentRows.map((row) => {
        if (row.variant.id !== draft.productVariantId) return row;
        const quantity = row.quantity + draft.quantity;
        return {
          ...row,
          cost: draft.unitCost,
          quantity,
          status: getStockStatus(quantity),
          variant: { ...row.variant, price: draft.price, inventory: { quantity } },
        };
      }));
      setCatalogProducts((currentProducts) => currentProducts.map((product) => ({
        ...product,
        productVariants: product.productVariants?.map((variant) => variant.id === draft.productVariantId
          ? { ...variant, price: draft.price, inventory: { quantity: (variant.inventory?.quantity ?? 0) + draft.quantity } }
          : variant),
      })));
      setDraftNotice(t('admin.products.draft.updated', {
        product: draft.productName,
        color: draft.color,
        version: draft.versionName,
        quantity: draft.quantity,
        note: draft.note,
      }));
    } else {
      const variantId = temporaryId.current--;
      const productId = draft.productId ?? temporaryId.current--;
      const newVariant: AdminProductVariant = {
        id: variantId,
        versionName: draft.versionName,
        color: draft.color,
        price: draft.price,
        inventory: { quantity: draft.quantity },
        purchaseReceipts: [{ receiptId: temporaryId.current--, unitCost: draft.unitCost }],
      };
      const existingProduct = catalogProducts.find((product) => product.id === draft.productId);
      const productForRow: AdminProduct = existingProduct
        ? { ...existingProduct, productVariants: [...(existingProduct.productVariants ?? []), newVariant] }
        : {
          id: productId,
          name: draft.productName,
          category: { name: draft.category.name },
          brand: { name: draft.brand.name },
          productVariants: [newVariant],
        };

      setProductRows((currentRows) => [{
        product: productForRow,
        variant: newVariant,
        cost: draft.unitCost,
        quantity: draft.quantity,
        status: getStockStatus(draft.quantity),
      }, ...currentRows]);
      setCatalogProducts((currentProducts) => existingProduct
        ? currentProducts.map((product) => product.id === existingProduct.id ? productForRow : product)
        : [productForRow, ...currentProducts]);
      if (!existingProduct) {
        setTotalElements((currentTotal) => currentTotal + 1);
        setPageProductCount((currentCount) => currentCount + 1);
      }
      setDraftNotice(t('admin.products.draft.created', {
        product: draft.productName,
        color: draft.color,
        version: draft.versionName,
        note: draft.note,
      }));
    }

    setIsStockModalOpen(false);
  };

  const { start: rangeStart, end: rangeEnd } = getPaginationRange(
    currentPage,
    pageSize,
    totalElements,
    pageProductCount,
  );
  const secondaryActionClassName = 'inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-outline-variant bg-white px-3 font-label-md text-secondary transition-colors hover:bg-surface-container-low focus:outline-none focus:ring-2 focus:ring-primary/20';

  return (
    <div className="mx-auto flex min-h-full w-full min-w-0 max-w-[1600px] flex-col gap-md sm:gap-lg">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="font-h1 text-h1 text-on-surface">{t('admin.products.title')}</h1>
          <p className="mt-1 text-body-md text-secondary">{t('admin.products.subtitle')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className={secondaryActionClassName}><span className="material-symbols-outlined text-[19px]">print</span>{t('admin.products.actions.print')}</button>
          <button type="button" className={secondaryActionClassName}><span className="material-symbols-outlined text-[19px]">file_download</span>{t('admin.products.actions.export')}</button>
          <button type="button" className={secondaryActionClassName}><span className="material-symbols-outlined text-[19px]">file_upload</span>{t('admin.products.actions.import')}</button>
          <button type="button" className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-primary px-4 font-label-md text-on-primary shadow-sm hover:bg-on-primary-fixed-variant sm:flex-none" onClick={() => setIsStockModalOpen(true)}>
            <span className="material-symbols-outlined text-[20px]">add</span>
            {t('admin.products.actions.add_stock')}
          </button>
        </div>
      </header>

      {draftNotice && (
        <div className="flex items-start justify-between gap-3 rounded-xl border border-primary/25 bg-primary-fixed/45 px-4 py-3 text-on-primary-fixed" role="status">
          <div className="flex gap-2 text-body-sm">
            <span className="material-symbols-outlined text-[19px] text-primary">info</span>
            <p><strong>{t('admin.products.draft.title')}</strong> {draftNotice} {t('admin.products.draft.server_notice')}</p>
          </div>
          <button type="button" className="rounded-lg p-1 text-secondary hover:bg-white/50" onClick={() => setDraftNotice('')} aria-label={t('admin.products.actions.close_notice')}>
            <span className="material-symbols-outlined text-[18px]">close</span>
          </button>
        </div>
      )}

      <section className="flex min-h-[520px] min-w-0 flex-1 flex-col overflow-hidden rounded-[20px] border border-outline-variant/70 bg-white shadow-sm">
        <div className="border-b border-outline-variant px-4 py-4 sm:px-5">
          <h2 className="font-h2 text-h2 text-on-surface">{t('admin.products.management_title')}</h2>
          <p className="mt-1 text-body-sm text-secondary">{t('admin.products.management_description')}</p>
        </div>
        <ProductToolbar
          searchValue={searchValue}
          selectedCategoryId={selectedCategoryId}
          selectedBrandId={selectedBrandId}
          selectedStockStatus={selectedStockStatus}
          categories={categories}
          brands={brands}
          isCategoryLoading={isCategoryLoading}
          isBrandLoading={isBrandLoading}
          onSearchChange={setSearchValue}
          onCategoryChange={setSelectedCategoryId}
          onBrandChange={setSelectedBrandId}
          onStockStatusChange={setSelectedStockStatus}
        />
        {(categoryError || brandError || (error && productRows.length > 0)) && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-error/20 bg-error-container/35 px-4 py-2 text-body-sm text-error" role="alert">
            <span>{[categoryError, brandError, error].filter(Boolean).join(' ')}</span>
            <button type="button" className="shrink-0 font-label-sm text-primary hover:underline" onClick={() => void Promise.all([loadProducts(currentPage), loadCatalogEntities()])}>
              {t('admin.products.actions.retry')}
            </button>
          </div>
        )}
        <ProductTable
          rows={filteredRows}
          currentPage={currentPage}
          totalPages={totalPages}
          totalElements={totalElements}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          isLoading={isLoading}
          isInitialLoading={!hasLoaded && isLoading}
          error={error}
          hasActiveFilters={hasActiveFilters}
          onPageChange={setCurrentPage}
          onRetry={() => void loadProducts(currentPage)}
          onClearFilters={clearFilters}
        />
      </section>

      <CatalogManagementSection
        brands={brands}
        categories={categories}
        isBrandLoading={isBrandLoading}
        isCategoryLoading={isCategoryLoading}
        brandError={brandError}
        categoryError={categoryError}
        pendingAction={pendingAction}
        onRefresh={loadCatalogEntities}
        onSaveBrand={saveBrand}
        onDeleteBrand={removeBrand}
        onSaveCategory={saveCategory}
        onDeleteCategory={removeCategory}
      />

      {isStockModalOpen && (
        <ProductStockModal products={catalogProducts} onClose={() => setIsStockModalOpen(false)} onSubmit={saveStockDraft} />
      )}
    </div>
  );
};

export default AdminProductsPage;
